import React, { useState, useEffect } from "react";
import { jwtDecode } from 'jwt-decode';
import { learningMaterialsAPI } from '../services/api';
import { 
  Button, ProgressBar, Form, 
  Row, Col, Accordion
} from 'react-bootstrap';
import AssignmentIcon from '../img/assign.gif';
import { 
  CheckCircleFill, FileEarmarkText, Download, 
  Trash, ExclamationTriangleFill 
} from 'react-bootstrap-icons';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setFeeStatus, setAuthState } from '../authSlice';
import LearningMaterialsGuideModal from "../Components/LearningMaterialsGuideModal.jsx";
import '../CSS/LearningMaterials.css';

function LearningMaterials() {
  // State management
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [selectedClass, setSelectedClass] = useState("6");
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [toast, setToast] = useState({ show: false, variant: '', message: '' });
  const [validationErrors, setValidationErrors] = useState({
    file: '',
    description: '',
    class: ''
  });
  const [accessDenied, setAccessDenied] = useState(false);
  const [denialReason, setDenialReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Hooks and Redux
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, token } = useSelector(state => state.auth);
  const { role, id, class: studentClass } = user;
  const feeStatus = useSelector(state => state.auth.feeStatus);

  const classes = [6, 7, 8, 9, 10, 11, 12];

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Check and restore auth state on component mount
  useEffect(() => {
    const restoreAuthState = async () => {
      try {
        const storedToken = localStorage.getItem('token') || 
                          localStorage.getItem(`${role}Token`) || 
                          localStorage.getItem('studentToken') || 
                          localStorage.getItem('staffToken') || 
                          localStorage.getItem('adminToken');

        if (storedToken && !token) {
          const decoded = jwtDecode(storedToken);
          const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}');
          
          dispatch(setAuthState({
            user: {
              role: decoded.role || storedUserData.role,
              id: decoded.id || storedUserData.id,
              class: decoded.class || storedUserData.class,
              email: decoded.email || storedUserData.email,
              name: decoded.name || storedUserData.name
            },
            token: storedToken
          }));
        }

        await fetchData();
      } catch (error) {
        console.error('Error restoring auth state:', error);
        setIsLoading(false);
      }
    };

    restoreAuthState();
  }, [dispatch]);

  // Data fetching
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token') || 
                   localStorage.getItem(`${role}Token`) || 
                   localStorage.getItem('studentToken') || 
                   localStorage.getItem('staffToken') || 
                   localStorage.getItem('adminToken');

      if (!token) {
        throw new Error('No authentication token found');
      }

      if (role === 'student') {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          throw new Error('Session expired. Please login again.');
        }

        const response = await learningMaterialsAPI.getStudentFeeStatus(id, token);
        
        const minFee = studentClass <= 9 ? 1800 : 2000;
        
        if (response.data.data.paid < minFee) {
          setAccessDenied(true);
          setDenialReason(`Minimum school fee payment of PGK ${minFee} required. You've paid PGK ${response.data.data.paid}.`);
          setIsLoading(false);
          return;
        }
        dispatch(setFeeStatus(response.data.data));
      }
      
      await fetchUploadedFiles();
    } catch (error) {
      console.error('Error:', error);
      
      let errorMessage = error.response?.data?.message || 'An error occurred';
      
      if (error.message.includes('No authentication token')) {
        errorMessage = 'Session expired. Please login again.';
      } else if (error.message.includes('Session expired')) {
        errorMessage = 'Session expired. Please login again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Invalid or expired token. Please login again.';
      }

      setToast({ show: true, variant: 'danger', message: errorMessage });

      if (error.response?.status === 403) {
        setAccessDenied(true);
        setDenialReason(error.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      const token = localStorage.getItem('token') || 
                   localStorage.getItem(`${role}Token`);

      if (!token) throw new Error('No authentication token found');

      const response = await learningMaterialsAPI.getFiles(token);
      const filesData = response.data.success ? response.data.data : [];
      const filesByClass = {};
      
      if (role === 'student') {
        filesByClass[studentClass] = [];
      } else {
        classes.forEach(cls => { filesByClass[cls] = []; });
      }

      if (filesData && Array.isArray(filesData)) {
        filesData.forEach(file => {
          const fileClass = file.class || "6";
          if (filesByClass[fileClass]) {
            filesByClass[fileClass].push({
              ...file,
              originalName: file.original_filename || file.filename.replace(/^\d{14}_\d+_/, ''),
              formattedDate: formatDate(file.uploadDate)
            });
          }
        });
      }

      setUploadedFiles(filesByClass);
    } catch (error) {
      console.error('Error fetching files:', error);
      setToast({
        show: true,
        variant: 'danger',
        message: error.response?.data?.message || 'Failed to load learning materials. Please try again later.'
      });
      throw error;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
  };

  const validateForm = () => {
    const allowedExtensions = new Set([
      '.jpg', '.jpeg', '.png', '.pdf', '.xls', '.xlsx', '.doc', '.docx', '.txt'
    ]);

    const mimeMap = {
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.pdf': 'application/pdf',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.txt': 'text/plain'
    };

    const errors = { file: '', description: '', class: '' };
    let isValid = true;

    if (!file) { errors.file = 'Please select a file to upload'; isValid = false; }
    if (!description.trim()) { errors.description = 'Please enter a description'; isValid = false; }
    if (!selectedClass) { errors.class = 'Please select a class'; isValid = false; }

    if (!file) { setValidationErrors(errors); return false; }

    const fileExtension = (file.name.match(/(\.[^.]+)$/)?.[1] || '').toLowerCase();
    console.log('Selected file:', file.name, 'Extension:', fileExtension, 'MIME:', file.type);

    if (!allowedExtensions.has(fileExtension)) {
      errors.file = `File extension ${fileExtension} is not allowed`;
      isValid = false;
    }

    const detectedMime = file.type || mimeMap[fileExtension] || 'application/octet-stream';
    const allowedMimeTypes = new Set(Object.values(mimeMap));
    if (!allowedMimeTypes.has(detectedMime)) {
      errors.file = `File type ${detectedMime} is not allowed`;
      isValid = false;
    }

    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.file = `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds 25MB limit`;
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const upload = async () => {
    if (!validateForm()) {
      setToast({ show: true, variant: 'danger', message: 'Please fix all validation errors before uploading' });
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("description", description);
    formData.append("class", selectedClass);
    console.log('Uploading file:', file.name, 'Size:', file.size, 'MIME:', file.type);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem(`${role}Token`);
      
      const response = await learningMaterialsAPI.uploadFile(
        formData,
        progressEvent => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(progress);
          if (progress === 100) setTimeout(() => setUploadProgress(0), 500);
        },
        token
      );

      setToast({ show: true, variant: 'success', message: response.data.message });
      setDescription("");
      setFile(null);
      setSelectedClass("6");
      setValidationErrors({ file: '', description: '', class: '' });
      await fetchUploadedFiles();
    } catch (err) {
      console.error('Upload error:', err);
      setToast({ show: true, variant: 'danger', message: err.response?.data?.message || 'Error uploading file' });
      setUploadProgress(0);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem(`${role}Token`);
      const response = await learningMaterialsAPI.downloadFile(filename, token);

      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = filename.replace(/^\d+_/, '');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches && matches[1]) downloadFilename = matches[1];
      }

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      setToast({ show: true, variant: 'success', message: 'File downloaded successfully' });
    } catch (error) {
      console.error('Download error:', error);
      setToast({
        show: true,
        variant: 'danger',
        message: error.response?.status === 404 ? 'File not found. It may have been deleted.' : 'Error downloading file'
      });
    }
  };

  const handleDelete = async (filename) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem(`${role}Token`);
      await learningMaterialsAPI.deleteFile(filename, token);
      setToast({ show: true, variant: 'success', message: 'File deleted successfully' });
      await fetchUploadedFiles();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({
        show: true,
        variant: 'danger',
        message: error.response?.status === 404 ? 'File not found. It may have been deleted already.' : 'Error deleting file'
      });
    }
  };

  // ── Loading state (inline, no dark background) ──
  if (isLoading) {
    return (
      <div className='container-fluid'>
        <div className='row'>
          <div className='dashboard d-flex flex-wrap'>
            <div className="dashboard-Container col">
              <div className="lm-loading-wrapper">
                <div className="lm-loader-ring"></div>
                <p className="lm-loader-text">Loading Materials...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Access denied ──
  if (accessDenied) {
    return (
      <div className='container-fluid' style={{ fontFamily: 'Poppins, sans-serif' }}>
        <div className='row'>
          <div className='dashboard d-flex flex-wrap'>
            <div className="dashboard-Container col">
              <div className="lm-access-denied m-4">
                <div className="lm-access-denied__icon">
                  <ExclamationTriangleFill size={36} />
                </div>
                <h4 className="lm-access-denied__heading">Access Denied</h4>
                <p className="lm-access-denied__reason">{denialReason}</p>
                {role === 'student' && (
                  <button className="lm-btn-home mt-2" onClick={() => navigate('/home')}>
                    Return Home
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className='container-fluid' style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div className='row'>
        <div className='dashboard d-flex flex-wrap'>

          <div className="dashboard-Container col">
            <div className="assignment-page container-fluid">

              {/* ── Page Header ── */}
              <div className='row my-4'>
                <div className='col-12'>
                  {(role === 'staff') && (
                    <div className="lm-info-alert mb-4">
                      <i className="bi bi-info-circle"></i>
                      You have view-only access as a staff member.
                    </div>
                  )}
                  <h3 className="d-flex align-items-center">
                    Learning Materials
                    <img className='animated-icons ms-2' src={AssignmentIcon} alt='assignment icon' width="40" />
                  </h3>
                </div>
              </div>

              {/* ── Custom Toast ── */}
              <div className="lm-toast-region">
                <div className={`lm-toast lm-toast--${toast.variant}${toast.show ? ' lm-toast--show' : ''}`}>
                  <div className="lm-toast__header">
                    <span className="lm-toast__title">
                      {toast.variant === 'success' ? 'Success' : 'Error'}
                    </span>
                    <button
                      className="lm-toast__close"
                      onClick={() => setToast(t => ({ ...t, show: false }))}
                      aria-label="Close"
                    >×</button>
                  </div>
                  <div className="lm-toast__body">
                    {toast.variant === 'success'
                      ? <CheckCircleFill />
                      : <ExclamationTriangleFill />
                    }
                    {toast.message}
                  </div>
                </div>
              </div>

              {/* ── Upload Section — Admin only ── */}
              {(role === 'admin') && (
                <div className='row mb-4'>
                  <div className='col-12'>
                    <div className="lm-upload-card">
                      <div className="lm-upload-card__header">
                        <span className="lm-upload-card__header-title">Upload New Material</span>
                      </div>
                      <div className="lm-upload-card__body">
                        <Form noValidate>
                          <Row>
                            <Col md={4}>
                              <Form.Group controlId="formClass" className="mb-3">
                                <Form.Select
                                  className="lm-form-select"
                                  value={selectedClass}
                                 onChange={(e) => {
                                    setSelectedClass(e.target.value);
                                    if (e.target.value) {
                                      setValidationErrors(prev => ({ ...prev, class: '' }));
                                    }
                                  }}
                                  isInvalid={!!validationErrors.class}
                                >
                                  <option value="">Select a class</option>
                                  {classes.map(cls => (
                                    <option key={cls} value={cls}>Class {cls}</option>
                                  ))}
                                </Form.Select>
                                {validationErrors.class && (
                                  <div className="lm-field-error">{validationErrors.class}</div>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={8}>
                              <Form.Group controlId="formDescription" className="mb-3">
                                <Form.Control
                                  className="lm-form-control"
                                  type="text"
                                  placeholder="Enter file description"
                                  value={description}
                                  onChange={(e) => {
                                  setDescription(e.target.value);
                                  if (e.target.value.trim()) {
                                    setValidationErrors(prev => ({ ...prev, description: '' }));
                                  }
                                }}
                                  isInvalid={!!validationErrors.description}
                                />
                                {validationErrors.description && (
                                  <div className="lm-field-error">{validationErrors.description}</div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                          <Form.Group controlId="formFile" className="mb-3">
                            <Form.Control
                              type="file"
                              onChange={(e) => {
                                if (e.target.files.length > 0) {
                                  const selectedFile = e.target.files[0];
                                  console.log('File selected:', selectedFile.name, 'Type:', selectedFile.type);
                                  setFile(selectedFile);
                                  setValidationErrors({ ...validationErrors, file: '' });
                                }
                              }}
                              className="lm-form-control lm-file-input"
                              isInvalid={!!validationErrors.file}
                              accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx,.txt"
                            />
                            {validationErrors.file && (
                            <div className="lm-field-error">{validationErrors.file}</div>
                          )}
                          </Form.Group>
                          <button
                            className="lm-btn-upload"
                            onClick={upload}
                            disabled={uploadProgress > 0}
                            type="button"
                          >
                            {uploadProgress > 0 ? 'Uploading...' : 'Upload Material'}
                          </button>
                        </Form>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Progress Bar ── */}
              {uploadProgress > 0 && (
                <div className="lm-progress-wrapper mb-4">
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                    striped
                    variant="success"
                    className="lm-progress-bar"
                  />
                </div>
              )}

              {/* ── Materials Accordion ── */}
              <div className="mt-5">
                <h4 className="lm-section-title mb-4">Available Materials</h4>

                {role === 'student' ? (
                  <Accordion defaultActiveKey={String(studentClass)} flush className="lm-accordion">
                    <Accordion.Item eventKey={String(studentClass)} className="lm-accordion__item">
                      <Accordion.Header className="lm-accordion__header">
                        <span className="fw-bold">Class {studentClass}</span>
                        <span className="lm-badge ms-2">
                          {uploadedFiles[studentClass]?.length || 0} files
                        </span>
                      </Accordion.Header>
                      <Accordion.Body className="lm-accordion__body">
                        {uploadedFiles[studentClass]?.length > 0 ? (
                          <Row>
                            {uploadedFiles[studentClass].map((file, index) => (
                              <Col key={index} lg={4} md={6} className="mb-3">
                                <div className="lm-file-card">
                                  <div className="lm-file-card__inner d-flex flex-column h-100">
                                    <div className="lm-file-card__header d-flex align-items-center mb-3">
                                      <div className="lm-file-icon-wrapper me-3">
                                        <FileEarmarkText className="lm-file-icon" />
                                      </div>
                                      <div className="overflow-hidden">
                                        <p className="lm-file-name mb-0">{file.originalName}</p>
                                        <small className="lm-file-date">Uploaded: {file.formattedDate}</small>
                                      </div>
                                    </div>
                                    <p className="lm-file-description flex-grow-1">{file.description}</p>
                                    <div className="lm-file-actions d-flex justify-content-between mt-2">
                                      <button
                                        className="lm-btn-action lm-btn-action--download"
                                        onClick={() => handleDownload(file.filename)}
                                        type="button"
                                      >
                                        <Download /> Download
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div className="lm-empty-state">
                            <FileEarmarkText className="lm-empty-state__icon" />
                            <p className="lm-empty-state__text">No materials available for your class</p>
                          </div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                ) : (
                  <Accordion defaultActiveKey="6" flush className="lm-accordion">
                    {classes.map(cls => (
                      <Accordion.Item eventKey={String(cls)} key={cls} className="lm-accordion__item">
                        <Accordion.Header className="lm-accordion__header">
                          <span className="fw-bold">Class {cls}</span>
                          <span className="lm-badge ms-2">
                            {uploadedFiles[cls]?.length || 0} files
                          </span>
                        </Accordion.Header>
                        <Accordion.Body className="lm-accordion__body">
                          {uploadedFiles[cls]?.length > 0 ? (
                            <Row>
                              {uploadedFiles[cls].map((file, index) => (
                                <Col key={index} lg={4} md={6} className="mb-3">
                                  <div className="lm-file-card">
                                    <div className="lm-file-card__inner d-flex flex-column h-100">
                                      <div className="lm-file-card__header d-flex align-items-center mb-3">
                                        <div className="lm-file-icon-wrapper me-3">
                                          <FileEarmarkText className="lm-file-icon" />
                                        </div>
                                        <div className="overflow-hidden">
                                          <p className="lm-file-name mb-0">{file.originalName}</p>
                                          <small className="lm-file-date">Uploaded: {file.formattedDate}</small>
                                        </div>
                                      </div>
                                      <p className="lm-file-description flex-grow-1">{file.description}</p>
                                      <div className="lm-file-actions d-flex justify-content-between mt-2">
                                        <button
                                          className="lm-btn-action lm-btn-action--download"
                                          onClick={() => handleDownload(file.filename)}
                                          type="button"
                                        >
                                          <Download /> Download
                                        </button>
                                        {(role === 'admin') && (
                                          <button
                                            className="lm-btn-action lm-btn-action--delete"
                                            onClick={() => handleDelete(file.filename)}
                                            type="button"
                                          >
                                            <Trash /> Delete
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          ) : (
                            <div className="lm-empty-state">
                              <FileEarmarkText className="lm-empty-state__icon" />
                              <p className="lm-empty-state__text">No materials available for Class {cls}</p>
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
    {role !== 'staff' && <LearningMaterialsGuideModal />}
    </>
  );
}

export default LearningMaterials;